import { ErrorHandler, NgModule, PLATFORM_ID, TransferState } from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';

import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { DatePipe, isPlatformServer, Location } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { environment } from '../environments/environment';
import { TranslateHttpLoader } from './ssr/TranslateHttpLoader';
import {
  PlatformurlPipe,
  PlatformassetPipe,
  PlatformInformationService
} from '../../projects/platform-manager/src/public_api';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RequestCache } from './core/services/request-cache.service';
import { UrlSerializer } from '@angular/router';
import { Myserializer } from './shared/services/myserializer';
import { HeaderComponent } from './layout/components/header/header.component';
import { SharedModule } from './shared/shared.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DeferLoadModule } from '@trademe/ng-defer-load';
import { IconComponent } from './shared/icons/icon/icon.component';
import { Error404Component } from './pages/error/error404/error404.component';
import { BASE_PATH } from './pages/product-selection/generated';
import { BASE_PATH as BOOKING_BASE_PATH } from './pages/booking/generated';
import {
  AbTestInterceptor,
  SimpleBookingInterceptor,
  CachingInterceptor,
  TokenInterceptorService
} from './core/interceptors';
import { SentryService } from './core/services/sentry.service';

export function createTranslateLoader(
  http: HttpClient,
  transferstate: TransferState,
  platformId: object,
  config: TranslateConfig,
  location: Location
) {
  if (isPlatformServer(platformId)) {
    return new TranslateHttpLoader(
      http,
      transferstate,
      platformId,
      location,
      config.path_server + '/assets/i18n/'
    );
  }
  return new TranslateHttpLoader(
    http,
    transferstate,
    platformId,
    location,
    config.path_client + '/assets/i18n/'
  );
}

export function errorHandlerProvider(sentryService: SentryService) {
  // TODO: we could let the customErrorHandler handle always and make the decision in handleError()
  // if (environment?.sentry_enabled) {
  //   return new CustomErrorHandler(sentryService);
  // }
  return new ErrorHandler();
}

export class CustomErrorHandler implements ErrorHandler {
  constructor(private sentryService: SentryService) {}
  handleError(error: Error) {
    this.sentryService.initSentry().then(t => {
      console.error(error); // still output issues
      t.report(error);
    });
  }
}

export class TranslateConfig {
  path_server: string;
  path_client: string;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    HeaderComponent,
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient, TransferState, PLATFORM_ID, TranslateConfig, Location]
      },
      useDefaultLang: false
    }),
    AppRoutingModule,
    CoreModule,
    SharedModule,
    DeferLoadModule,
    // set me to enabled: environment.production when I go live
    ServiceWorkerModule.register('/assetz/ngsw-worker.js', {
      scope: '/',
      enabled: true
    }),
    // header icon import, providers at component level
    IconComponent,
    // --------------
    PlatformassetPipe,
    Error404Component
  ],
  providers: [
    DatePipe,
    PlatformurlPipe,
    PlatformassetPipe,
    RequestCache,
    { provide: ErrorHandler, useFactory: errorHandlerProvider, deps: [SentryService] },
    { provide: UrlSerializer, useClass: Myserializer },
    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AbTestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SimpleBookingInterceptor, multi: true },
    {
      provide: TranslateConfig,
      useValue: { path_server: environment.language_path_server, path_client: environment.language_path }
    },
    {
      provide: BOOKING_BASE_PATH,
      useValue: environment.booking_hostname
    },
    {
      provide: BASE_PATH,
      useFactory: (platformService: PlatformInformationService) => {
        // when we want the product/cart apis to point somewhere else, change me
        return platformService.getCurrentPlatform();
      },
      deps: [PlatformInformationService]
    },
    provideClientHydration()
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {}
}
